import React, {useState} from "react";
import toast from "react-hot-toast";
import {Input} from "../../../components";
import {RouteService} from "../../../services";
import {sanitizeToDecimal} from "../../../utils";
import ClientSearch from "./select-search";

const DEFAULT_COMMISSION = 11.2;

const format = (amount) => {
  return parseFloat(parseFloat(amount || 0).toFixed(2));
};

const ModalCreateRoute = ({onClose, onSuccess, initialData}) => {
  const {form, isLoading, onChange, submit, derived} = useForm({
    initialData,
    onSuccess,
    onClose,
  });
  const isEdit = !!initialData?.id;

  return (
    <div className="w-[48rem] bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4">
        <h2 className="text-lg font-bold text-text">
          {isEdit ? "Editar ruta" : "Crear nueva ruta"}
        </h2>
        <button
          type="button"
          className="p-1.5 rounded-full hover:bg-gray5 transition-colors"
          onClick={onClose}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 text-text"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 px-6">
        <Input
          className={{base: "col-span-2"}}
          label="Nombre de ruta"
          value={form.name}
          onValueChange={(v) => onChange("name", v)}
        />
        <div className="flex items-end gap-3 pb-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 accent-green"
              checked={Boolean(form.is_in)}
              onChange={() => onChange("is_in", !form.is_in)}
            />
            <span className="text-sm text-text select-none">Entrada</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 accent-green"
              checked={Boolean(form.is_out)}
              onChange={() => onChange("is_out", !form.is_out)}
            />
            <span className="text-sm text-text select-none">Salida</span>
          </label>
        </div>

        {/* Facturación: Editable */}
        <Input
          label="Facturación por traslado ($)"
          value={form.cost}
          onValueChange={(v) => onChange("cost", sanitizeToDecimal(v, 2))}
        />

        <div className="space-y-1">
          <p className="text-text">Cliente</p>
          <ClientSearch
            placeholder="Buscar cliente..."
            value={form.client_id}
            selectedLabel={form.client_name}
            onSelect={(id, name) => {
              onChange("client_id", id);
              onChange("client_name", name);
            }}
          />
        </div>

        {/* Deducción: Automático en $, no editable */}
        <Input
          label={`Deducción (${derived.deductionPerc}%) ($)`}
          value={derived.deductionAmount}
          disabled={true}
        />

        {/* Comisión Conductor: Editable */}
        <Input
          label="Comisión de conductor ($)"
          value={form.commission_amount_driver}
          onValueChange={(v) =>
            onChange("commission_amount_driver", sanitizeToDecimal(v, 2))
          }
        />

        {/* Comisión Propietario: Editable */}
        <Input
          label="Comisión de propietario ($)"
          value={form.commission_amount_owner}
          onValueChange={(v) =>
            onChange("commission_amount_owner", sanitizeToDecimal(v, 2))
          }
        />

        {/* Comisión Empresa: Automático, no editable */}
        <Input
          label="Comisión de empresa ($)"
          value={derived.companyComm}
          disabled={true}
        />

        <Input
          type="date"
          label="Fecha de próximo ajuste"
          value={form.next_adjustment_at}
          onValueChange={(v) => onChange("next_adjustment_at", v)}
        />
      </div>

      <div className="flex justify-center mt-2 px-6 py-4 gap-3">
        <button
          type="button"
          className="px-6 py-2 bg-gray-200 text-text font-semibold rounded-full hover:bg-gray-300 transition-colors text-sm"
          onClick={onClose}
          disabled={isLoading}
        >
          Cerrar
        </button>
        <button
          type="button"
          className="px-6 py-2 bg-red text-white font-semibold rounded-full hover:bg-gray-800 transition-colors text-sm disabled:opacity-50"
          onClick={submit}
          disabled={isLoading}
        >
          {isLoading ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </div>
  );
};

const useForm = ({initialData, onSuccess, onClose}) => {
  const getInitial = (data) => ({
    name: data?.name || "",
    cost: String(data?.cost || ""),
    client_id: data?.client_id || null,
    client_name:
      data?.client?.person?.company?.name ||
      data?.client?.person?.fullName ||
      "",
    deduction_perc: String(
      data?.deduction_perc ?? DEFAULT_COMMISSION.toFixed(2),
    ),
    commission_amount_driver: String(data?.commission_amount_driver || ""),
    commission_amount_owner: String(data?.commission_amount_owner || ""),
    next_adjustment_at: data?.next_adjustment_at || "",
    is_in: data?.is_in || 0,
    is_out: data?.is_out || 0,
  });

  const [form, setForm] = useState(() => getInitial(initialData));
  const [isLoading, setIsLoading] = useState(false);

  // Cálculos dinámicos
  const costVal = parseFloat(form.cost) || 0;
  const deductionPerc = parseFloat(form.deduction_perc) || DEFAULT_COMMISSION;
  const deductionAmount = parseFloat(
    ((costVal * deductionPerc) / 100).toFixed(2),
  );
  const driverComm = parseFloat(form.commission_amount_driver) || 0;
  const ownerComm = parseFloat(form.commission_amount_owner) || 0;

  // Remanente asignado a la empresa
  const companyComm = parseFloat(
    (costVal - deductionAmount - driverComm - ownerComm).toFixed(2),
  );

  const onChange = (target, value) => {
    setForm((s) => ({...s, [target]: value}));
  };

  const isValidForm = () => {
    const onError = (msg) => {
      toast.error(msg);
      return false;
    };

    if (!form.name.trim()) return onError("El nombre de la ruta es requerida");
    if (!form.is_in && !form.is_out)
      return onError("Debe seleccionar entrada o salida");
    if (!form.cost) return onError("El valor es requerido");
    if (!form.client_id) return onError("El cliente es requerido");
    if (!form.next_adjustment_at)
      return onError("La fecha de próximo ajuste es requerida");

    if (companyComm < 0) {
      return onError(
        "La suma de comisiones y deducción no puede ser MAYOR a la facturación del traslado.",
      );
    }

    return true;
  };

  const submit = async () => {
    if (!isValidForm()) return;
    setIsLoading(true);
    try {
      const payload = {
        ...form,
        cost: format(costVal),
        deduction_perc: format(deductionPerc),
        deduction_amount: format(deductionAmount),
        commission_amount_driver: format(driverComm),
        commission_amount_owner: format(ownerComm),
        commission_amount_company: format(companyComm),
        is_in: Number(form.is_in),
        is_out: Number(form.is_out),
      };

      if (initialData?.id) {
        await RouteService.admin.update({...payload, id: initialData.id});
        toast.success("Ruta actualizada con éxito");
      } else {
        await RouteService.admin.create(payload);
        toast.success("Ruta creada con éxito");
      }
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.log(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    isLoading,
    onChange,
    submit,
    derived: {deductionPerc, deductionAmount, companyComm},
  };
};

export default ModalCreateRoute;
